import { Component, OnInit } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { Observable } from 'rxjs';
import { DashboardService } from './services/dashboard.service';
import { DashboardDto, ProgressCard } from './models/dashboard.model';

@Component({
  selector: 'app-my-dashboard',
  standalone: true,
  imports: [TranslateModule, CommonModule],
  templateUrl: './my-dashboard.component.html',
  styleUrl: './my-dashboard.component.css'
})
export class MyDashboardComponent implements OnInit {
  dashboardData: DashboardDto | null = null;
  isLoading = false;
  error: string | null = null;

  // Progress cards data
  newPartnersProgress: ProgressCard | null = null;
  newAgreementsProgress: ProgressCard | null = null;
  completedEventsProgress: ProgressCard | null = null;
  totalCollectedAmountProgress: ProgressCard | null = null;

  constructor(private dashboardService: DashboardService) {}

  ngOnInit(): void {
    this.loadDashboardData();
  }

  loadDashboardData(): void {
    this.isLoading = true;
    this.error = null;

    this.dashboardService.getDashboard().subscribe({
      next: (response) => {
        if (response.success && response.value) {
          this.dashboardData = response.value;
          this.calculateProgressCards();
        } else {
          this.error = 'Failed to load dashboard data';
        }
        this.isLoading = false;
      },
      error: (err) => {
        console.error('Error loading dashboard data:', err);
        this.error = 'Error loading dashboard data';
        this.isLoading = false;
      }
    });
  }

  private calculateProgressCards(): void {
    if (!this.dashboardData) return;

    // New Partners Progress (using some realistic targets for demonstration)
    const partnersTarget = 50;
    const partnersAchieved = this.dashboardData.newPartners || 0;
    this.newPartnersProgress = {
      title: 'dashboard.newPartnersCount',
      percentage: Math.min((partnersAchieved / partnersTarget) * 100, 100),
      progressClass: partnersAchieved >= partnersTarget ? 'success' : '',
      achieved: partnersAchieved,
      target: partnersTarget,
      currentCount: partnersAchieved,
      totalCount: partnersTarget
    };

    // New Agreements Progress
    const agreementsTarget = 25;
    const agreementsAchieved = this.dashboardData.agreementsCount || 0;
    this.newAgreementsProgress = {
      title: 'dashboard.newAgreementsCount',
      percentage: Math.min((agreementsAchieved / agreementsTarget) * 100, 100),
      progressClass: agreementsAchieved >= agreementsTarget ? 'success' : '',
      achieved: agreementsAchieved,
      target: agreementsTarget,
      currentCount: agreementsAchieved,
      totalCount: agreementsTarget
    };

    // Completed Events Progress (using active agreements as proxy)
    const eventsTarget = 20;
    const eventsAchieved = this.dashboardData.activeAgreements || 0;
    this.completedEventsProgress = {
      title: 'dashboard.completedEventsCount',
      percentage: Math.min((eventsAchieved / eventsTarget) * 100, 100),
      progressClass: eventsAchieved >= eventsTarget ? 'success' : eventsAchieved > 0 ? '' : 'danger',
      achieved: eventsAchieved,
      target: eventsTarget,
      currentCount: eventsAchieved,
      totalCount: eventsTarget
    };

    // Total Collected Amount Progress
    const donationTarget = 100000;
    const donationAchieved = Number(this.dashboardData.totalDonationsValue) || 0;
    this.totalCollectedAmountProgress = {
      title: 'dashboard.totalCollectedAmount',
      percentage: Math.min((donationAchieved / donationTarget) * 100, 100),
      progressClass: donationAchieved >= donationTarget ? 'success' : donationAchieved > 0 ? 'warning' : 'danger',
      achieved: donationAchieved,
      target: donationTarget,
      currentCount: donationAchieved,
      totalCount: donationTarget
    };
  }

  getIndicatorsCount(): number {
    return this.dashboardData?.newPartners || 10;
  }

  retry(): void {
    this.loadDashboardData();
  }
}
